
import { supabase } from './authService';
import { Language, CommentWithUser } from '../types';
import { fetchTeamInfo, getTeamLogo } from './footballService';

const augmentCommentsWithFavoriteTeam = async (comments: any[]): Promise<any[]> => {
  if (!comments || comments.length === 0) return [];
  
  const userIds = Array.from(new Set(comments.map(c => c.user_id)));
  
  const { data: selections } = await supabase
    .from('teams_selections')
    .select('user_id, team_id')
    .in('user_id', userIds)
    .eq('is_favorite', true);

  const teamMap = new Map();
  if (selections) {
    const teamIds = Array.from(new Set(selections.map(s => s.team_id)));
    const teamInfos = await Promise.all(teamIds.map(id => fetchTeamInfo(id)));
    
    const teamDataMap = new Map();
    teamInfos.forEach(info => {
      if (info) {
        const teamId = info.team.id.toString();
        teamDataMap.set(teamId, {
          name: info.team.name,
          logo: getTeamLogo(teamId)
        });
      }
    });

    selections.forEach(s => {
      teamMap.set(s.user_id, teamDataMap.get(s.team_id.toString()));
    });
  }

  return comments.map(c => ({
    ...c,
    user: c.user ? {
      ...c.user,
      favorite_team: teamMap.get(c.user_id) || null
    } : undefined
  }));
};

/**
 * 특정 뉴스의 댓글 목록을 가져옵니다.
 */
export const fetchNewsComments = async (newsId: string, currentLang: Language): Promise<(CommentWithUser & { is_liked?: boolean })[]> => {
  if (!newsId || newsId.startsWith('nd-')) return [];

  try {
    const { data: { user: authUser } } = await supabase.auth.getUser();

    const { data: comments, error: cError } = await supabase
      .from('news_comments')
      .select('*, news_comments_likes(user_id)')
      .eq('news_id', newsId)
      .eq('lang', currentLang)
      .eq('is_deleted', false)
      .order('created_at', { ascending: true });

    if (cError) throw cError;
    if (!comments || comments.length === 0) return [];

    const userIds = Array.from(new Set(comments.map(c => c.user_id)));
    const { data: users, error: uError } = await supabase
      .from('user')
      .select('id, name, avatar_url')
      .in('id', userIds);

    if (uError) console.warn("Could not fetch user profiles for comments:", uError.message);

    const userMap = new Map((users || []).map(u => [u.id, u]));

    const commentsWithUser = comments.map(item => ({
      ...item,
      user: userMap.get(item.user_id) || { name: 'Unknown User', avatar_url: '' }
    }));

    const augmented = await augmentCommentsWithFavoriteTeam(commentsWithUser);

    return augmented.map(item => {
      const likes = item.news_comments_likes || [];
      return {
        ...item,
        post_id: item.news_id,
        likes_count: likes.length,
        is_liked: authUser ? likes.some((l: any) => l.user_id === authUser.id) : false,
        is_reported: item.is_reported || false
      };
    }) as (CommentWithUser & { is_liked?: boolean })[];

  } catch (err: any) {
    console.error("fetchNewsComments error:", err?.message || err);
    return [];
  }
};

/**
 * 특정 사용자가 작성한 모든 뉴스 댓글을 가져옵니다.
 */
export const fetchUserNewsComments = async (userId: string): Promise<CommentWithUser[]> => {
  try {
    const { data: comments, error: cError } = await supabase
      .from('news_comments')
      .select('*')
      .eq('user_id', userId)
      .eq('is_deleted', false)
      .order('created_at', { ascending: false });

    if (cError) throw cError;
    if (!comments || comments.length === 0) return [];

    const { data: profile } = await supabase
      .from('user')
      .select('name, avatar_url')
      .eq('id', userId)
      .single();

    const commentsWithUser = comments.map(item => ({
      ...item,
      user: profile || { name: 'User', avatar_url: '' }
    }));

    const augmented = await augmentCommentsWithFavoriteTeam(commentsWithUser);

    return augmented.map(item => ({
      ...item,
      post_id: item.news_id,
      likes_count: 0,
      is_reported: item.is_reported || false
    })) as CommentWithUser[];
  } catch (err: any) {
    return [];
  }
};

/**
 * 새 댓글을 작성합니다.
 */
export const createNewsComment = async ({
  newsId,
  lang,
  content,
  parentId = null,
}: {
  newsId: string;
  lang: Language;
  content: string;
  parentId?: number | string | null;
}) => {
  const { data: { user: authUser } } = await supabase.auth.getUser();
  if (!authUser) throw new Error('Login required');

  const payload: any = {
    news_id: newsId,
    lang: lang,
    user_id: authUser.id,
    content: content,
    is_deleted: false
  };

  if (parentId) {
    payload.parent_id = Number(parentId);
  }

  const { data: insertData, error: insertError } = await supabase
    .from('news_comments')
    .insert(payload)
    .select('*')
    .single();

  if (insertError) throw insertError;

  const { data: profile } = await supabase
    .from('user')
    .select('name, avatar_url')
    .eq('id', authUser.id)
    .single();
  
  const commentWithUser = {
    ...insertData,
    user: profile || { name: 'User', avatar_url: '' }
  };

  const augmented = await augmentCommentsWithFavoriteTeam([commentWithUser]);
  
  return { 
    ...augmented[0], 
    post_id: insertData.news_id,
    likes_count: 0,
    is_liked: false,
    is_reported: false
  } as (CommentWithUser & { is_liked: boolean });
};

export const deleteNewsComment = async (commentId: number | string) => {
  const { error } = await supabase
    .from('news_comments')
    .update({ is_deleted: true })
    .eq('id', commentId);
  
  if (error) throw error;
};

/**
 * 뉴스 좋아요 토글
 */
export const toggleNewsLike = async (newsId: string) => {
  const { data: { user } } = await supabase.auth.getUser();
  if (!user) throw new Error('Login required');

  // 현재 좋아요 상태 확인
  const { data: existingLike } = await supabase
    .from('news_likes')
    .select('*')
    .eq('news_id', newsId)
    .eq('user_id', user.id)
    .maybeSingle();

  if (existingLike) {
    // 좋아요 취소
    const { error } = await supabase
      .from('news_likes')
      .delete()
      .eq('news_id', newsId)
      .eq('user_id', user.id);
    if (error) throw error;
    return false; // unliked
  } else {
    // 좋아요 추가
    const { error } = await supabase
      .from('news_likes')
      .insert({
        news_id: newsId,
        user_id: user.id
      });
    if (error) throw error;
    return true; // liked
  }
};

/**
 * 뉴스 댓글을 신고합니다. (새로운 스키마 news_comments_id 반영)
 */
export const reportNewsComment = async (commentId: number | string, reason: string) => {
  const { data: { user } } = await supabase.auth.getUser();
  if (!user) throw new Error('Unauthorized');

  const { error } = await supabase.from('news_comments_reports').insert({
    news_comments_id: commentId,
    reporter_id: user.id,
    reason
  });
  
  if (error) {
    // 이미 신고된 경우(23505)를 포함하여 에러 객체를 그대로 던져 UI에서 처리하게 함
    throw error;
  }

  // 신고 성공 시 해당 댓글의 신고 상태 업데이트
  await supabase.from('news_comments').update({ is_reported: true }).eq('id', commentId);
};

/**
 * 뉴스 댓글 좋아요
 */
export const likeNewsComment = async (commentId: number | string) => {
  const { data: { user } } = await supabase.auth.getUser();
  if (!user) throw new Error('Login required');

  const { error } = await supabase.from('news_comments_likes').insert({
    news_comments_id: commentId,
    user_id: user.id
  });
  
  if (error) throw error;
};

/**
 * 뉴스 댓글 좋아요 취소
 */
export const unlikeNewsComment = async (commentId: number | string) => {
  const { data: { user } } = await supabase.auth.getUser();
  if (!user) throw new Error('Login required');

  const { error } = await supabase.from('news_comments_likes').delete()
    .eq('news_comments_id', commentId)
    .eq('user_id', user.id);
    
  if (error) throw error;
};
